import { Text } from "preact-i18n";

/**
 * Sum of the price adjustments for the selected modifier values
 * @param {object[]} modifiers
 * @param {object} modifierValues modifier id -> value ids (LIST) or [text] (TEXT)
 */
export function getModifierTotal(modifiers, modifierValues) {
  return (modifiers || []).reduce((total, modifier) => {
    const selected = modifierValues?.[modifier.id] || [];

    if (modifier.type === "TEXT") {
      return total + (selected[0] ? modifier.price : 0);
    }

    return (
      total +
      modifier.values
        .filter((value) => selected.includes(value.id))
        .reduce((sum, value) => sum + value.price, 0)
    );
  }, 0);
}

/**
 * Converts selected modifier values into unified order selectedAddons
 */
export function getSelectedAddons(modifiers, modifierValues, currencyCode) {
  return (modifiers || [])
    .map((modifier) => {
      const selected = modifierValues?.[modifier.id] || [];
      const values =
        modifier.type === "TEXT"
          ? selected.filter(Boolean).map((text) => ({
              name: text,
              costAdjustment: { value: modifier.price, currencyCode },
            }))
          : modifier.values
              .filter((value) => selected.includes(value.id))
              .map((value) => ({
                name: value.label,
                costAdjustment: { value: value.price, currencyCode },
              }));

      return { type: modifier.type, attribute: modifier.label, values };
    })
    .filter((addon) => addon.values.length);
}

const ProductPayLinkModifier = ({ modifier, value, format, onChange }) => {
  const required = modifier.min >= 1;
  const inputId = `ppl-modifier-${modifier.id}`;
  const optional = !required ? (
    <div className="ppl-modifier-hint">
      <Text id="PRODUCT_PAYLINK_OPTIONAL">Optional</Text>
    </div>
  ) : null;

  if (modifier.type === "TEXT") {
    const text = value[0] || "";
    const maxLength = modifier.max || undefined;

    return (
      <div className="ppl-modifier">
        <div className="ppl-modifier-heading">
          <label className="ppl-field-label" htmlFor={inputId}>
            {modifier.label}
          </label>
          {modifier.price ? <span className="ppl-modifier-price">+{format(modifier.price)}</span> : null}
        </div>
        {optional}
        <textarea
          id={inputId}
          className="ppl-textarea"
          rows={4}
          maxLength={maxLength}
          value={text}
          placeholder={maxLength ? `Up to ${maxLength} characters` : ""}
          onInput={(e) => onChange(e.target.value ? [e.target.value] : [])}
        />
        {maxLength ? (
          <div className="ppl-modifier-hint">
            {text.length}/{maxLength} characters
          </div>
        ) : null}
      </div>
    );
  }

  // list: radios when exactly one can be picked, checkboxes otherwise
  const single = modifier.max === 1;
  const hint = required ? (
    <div className="ppl-modifier-hint">
      {modifier.max && modifier.max > modifier.min ? (
        <Text id="PRODUCT_PAYLINK_CHOOSE_UP_TO" fields={{ count: modifier.max }}>
          Choose up to {modifier.max}
        </Text>
      ) : (
        <Text id="PRODUCT_PAYLINK_CHOOSE" fields={{ count: modifier.min }}>
          Choose {modifier.min}
        </Text>
      )}
    </div>
  ) : modifier.max && modifier.max > 1 ? (
    <div className="ppl-modifier-hint">
      <Text id="PRODUCT_PAYLINK_OPTIONAL">Optional</Text> ·{" "}
      <Text id="PRODUCT_PAYLINK_CHOOSE_UP_TO" fields={{ count: modifier.max }}>
        Choose up to {modifier.max}
      </Text>
    </div>
  ) : (
    optional
  );

  function toggle(id) {
    if (single) {
      // optional radios can be cleared by clicking the selected one again
      onChange(value.includes(id) && !required ? [] : [id]);
    } else if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else if (!modifier.max || value.length < modifier.max) {
      onChange([...value, id]);
    }
  }

  return (
    <fieldset className="ppl-modifier ppl-choice-group">
      <legend className="ppl-field-label">
        {modifier.label}
        {required ? (
          <span className="ppl-required" aria-hidden="true">
            {" "}*
          </span>
        ) : null}
      </legend>
      {hint}
      {modifier.values.map((v) => (
        <label className="ppl-choice">
          <input
            type={single ? "radio" : "checkbox"}
            name={inputId}
            checked={value.includes(v.id)}
            disabled={!single && !value.includes(v.id) && modifier.max && value.length >= modifier.max}
            onClick={() => toggle(v.id)}
          />
          <span className="ppl-choice-label">{v.label}</span>
          {v.price ? <span className="ppl-choice-price">+{format(v.price)}</span> : null}
        </label>
      ))}
    </fieldset>
  );
};

export default ProductPayLinkModifier;
